import { LinkButton, Pagination, SearchInput, Text, Tooltip } from '$components'
import { BinIcon, EyeIcon, PlusIcon } from '$components'
import { useDebouncedSearch } from '$hooks/use-debounced-search'
import { ElementIntent } from '$components'
import { createColumnHelper } from '@tanstack/react-table'
import { ClientsFilterParam } from '../enums/filter-params'
import { PetsCell } from '../components/pets-cell'
import { PageLayout } from 'src/layouts/page'
import { usePage } from '$hooks/use-page'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { clientDeleteMutation, clientIndexOptions, clientIndexQueryKey } from '$api-client/@tanstack/react-query.gen'
import { AnimatedOutlet } from '$components'
import { ClientRoutePath } from '../routes/protected'
import { Router } from '$utils/router'
import { Client, Role } from '$api-client/types.gen'
import { useMemo } from 'react'
import { ConfirmationModal } from '$components'
import { useNavigate } from 'react-router'
import { useAuth } from '$features/auth/hooks/use-auth'
import { PermissionsHelper } from '$utils/permissions'
import { GenericTable } from '$components'
import { OptionsCell } from '$components'
import { useModalState } from '$components'
import { TextCell } from '$components'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'
import { PersonCell } from '$components'
import { useTableSort } from '$hooks/use-table-sort'
import { ToastType } from '$components'
import { ToastHelper } from '$components'
import { usePageTitle } from '$components'
import { NoClientsEmptyState } from '../components/no-clients-empty-state'
import { TableSkeleton } from '$components'

const columnHelper = createColumnHelper<Client>()

export function ClientsPage() {
  usePageTitle('Clients')

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { page, setPage } = usePage()
  const { user } = useAuth()
  const { getSorting } = useTableSort()
  const { hasSubscriptionExpired } = useTenant()
  const {
    modalState: deleteConfirmationModalState,
    open: openDeleteConfirmationModal,
    close: closeDeleteConfirmationModal,
  } = useModalState()

  const { search, setSearch, debouncedSearch } = useDebouncedSearch({
    searchParam: ClientsFilterParam.Search,
    initialValue: '',
    onSearchChange: () => setPage(1),
  })

  const { data: clients, isLoading: isClientsLoading } = useQuery({
    ...clientIndexOptions({
      query: {
        query: debouncedSearch,
        page: page,
        sort: getSorting(),
      },
    }),
  })

  const { mutate: deleteClient, isPending: isDeletingClient } = useMutation({
    ...clientDeleteMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientIndexQueryKey() })
      closeDeleteConfirmationModal()
      ToastHelper.deleteSuccess(ToastType.Client)
    },
    onError: () => ToastHelper.deleteError(ToastType.Client),
  })

  const handleDeleteClient = () => {
    deleteClient({
      path: {
        id: deleteConfirmationModalState.modelId,
      },
    })
  }

  const columns = [
    columnHelper.accessor('first_name', {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => <PersonCell data={row.original} />,
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      cell: ({ row }) => (
        <TextCell>
          {row.original.email}
        </TextCell>
      ),
    }),
    columnHelper.accessor('phone', {
      header: 'Phone Number',
      cell: ({ row }) => (
        <TextCell>
          {row.original.phone}
        </TextCell>
      ),
    }),
    columnHelper.accessor('pets', {
      header: 'Pets',
      cell: ({ getValue }) => <PetsCell pets={getValue()} />,
    }),
    // columnHelper.accessor('tags', {
    //   header: 'Tags',
    //   cell: ({ getValue }) => <TagsCell tags={getValue()} />,
    // }),
    // columnHelper.accessor('lastAppointment', {
    //   header: 'Last Appointment',
    //   cell: ({ getValue }) => <DateCell date={getValue()} />,
    // }),
    columnHelper.display({
      id: 'actions',
      cell: ({ row }) => (
        <OptionsCell
          options={[
            {
              id: 'view',
              value: 'View',
              icon: EyeIcon,
              onClick: () => navigate(Router.getRoute(ClientRoutePath.ClientsView, { id: row.original.id })),
            },
            {
              id: 'delete',
              value: 'Delete',
              intent: ElementIntent.Danger,
              onClick: () => openDeleteConfirmationModal(row.original.id),
              icon: BinIcon,
              disabled: PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN]),
              tooltip: PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
                ? 'You are not allowed to delete clients.'
                : undefined,
            },
          ]}
        />
      ),
    }),
  ]

  const data = useMemo(() => clients?.data ?? [], [clients])

  return (
    <PageLayout>
      {/* Header */}
      <div className="grid grid-cols-1 grid-rows-[auto_1fr] h-full">
        <div className="pb-4 flex sm:flex-row flex-col sm:items-center justify-between gap-4 sm:gap-2">
          <Text
            as="h1"
            weight="medium"
          >
            Clients
          </Text>

          <div className="flex gap-2 items-center">
            <SearchInput
              value={search}
              onChange={setSearch}
              className="w-full sm:w-52"
            />

            <Tooltip
              content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined}
              disabled={!hasSubscriptionExpired}
            >
              <LinkButton
                to={ClientRoutePath.ClientsCreate}
                icon={PlusIcon}
                disabled={hasSubscriptionExpired}
              >
                New Client
              </LinkButton>
            </Tooltip>
          </div>
        </div>

        {/* Page Content */}
        {isClientsLoading && <TableSkeleton
          columns={5}
          rows={10}
        />}

        {!isClientsLoading && data.length === 0 && (
          <NoClientsEmptyState isNoResults={debouncedSearch.length > 0} />
        )}

        {data.length > 0 && (
          <GenericTable.Container>
            <GenericTable
              data={data}
              columns={columns}
              rowLink={row => Router.getRoute(ClientRoutePath.ClientsView, { id: row.id })}
            />

            <Pagination
              currentPage={page}
              onChange={setPage}
              totalPages={clients?.meta.last_page}
              totalItems={clients?.meta.total}
              itemsPerPage={clients?.meta.per_page}
              currentItemCount={clients?.meta.current_page}
            />
          </GenericTable.Container>
        )}
      </div>

      <AnimatedOutlet />

      {deleteConfirmationModalState.isOpen && (
        <ConfirmationModal
          intent={ElementIntent.Danger}
          onClose={closeDeleteConfirmationModal}
          onConfirm={handleDeleteClient}
          isLoading={isDeletingClient}
          title="Delete Client"
          description="Are you sure you want to delete this client?"
        />
      )}
    </PageLayout>
  )
}
